import { type ColumnDef, DataTable, EmptyState, ErrorState, Spinner } from '@olytic/foundation';
import { useEffect, useMemo, useState } from 'react';

import { type Delivery, loadDelivery } from './opportunityPageApi';
import { deliveryRows, type DeliveryRow, hours, monthLabel } from './pageModel';

/** The Delivery tab: the hours the deal needs by role and month, who is named to them, and what is unstaffed. */
export function DeliveryTab({ opportunityId }: { opportunityId: string }): JSX.Element {
  const [load, setLoad] = useState<{ state: 'loading' } | { state: 'error'; message: string } | { state: 'ready'; delivery: Delivery }>({ state: 'loading' });
  const [attempt, setAttempt] = useState(0);
  const [collapsed, setCollapsed] = useState<Set<string>>(new Set());

  useEffect(() => {
    let live = true;
    setLoad({ state: 'loading' });
    loadDelivery(opportunityId)
      .then((d) => { if (live) setLoad({ state: 'ready', delivery: d }); })
      .catch((e: unknown) => { if (live) setLoad({ state: 'error', message: e instanceof Error ? e.message : 'Could not load.' }); });
    return () => { live = false; };
  }, [opportunityId, attempt]);

  const delivery = load.state === 'ready' ? load.delivery : null;
  const columns = useMemo<ColumnDef<DeliveryRow>[]>(
    () => (delivery
      ? [
          { key: 'label', label: 'Role', nowrap: true, sortable: false },
          ...delivery.months.map((m, i): ColumnDef<DeliveryRow> => ({
            key: m,
            label: monthLabel(m, i === 0),
            align: 'right',
            figures: true,
            sortable: false,
            render: (r) => hours(r.hours[i]),
          })),
          { key: 'total', label: 'Total', align: 'right', figures: true, sortable: false, render: (r) => hours(r.total) },
        ]
      : []),
    [delivery],
  );

  if (load.state === 'loading') return <Spinner label="Loading delivery…" />;
  if (load.state === 'error') return <ErrorState title="Could not load delivery" description={load.message} onRetry={() => setAttempt((n) => n + 1)} />;
  if (load.delivery.roles.length === 0) {
    return <EmptyState title="No hours yet" description="Hours come from the primary quote's schedule once its lines have roles." />;
  }

  return (
    <DataTable<DeliveryRow>
      columns={columns}
      rows={deliveryRows(load.delivery, collapsed)}
      getRowId={(r) => r.id}
      treeColumn="label"
      rowDepth={(r) => r.depth}
      rowExpansion={(r) => {
        if (!r.role) return null;
        const roleId = r.id.slice('role:'.length);
        return {
          expanded: !collapsed.has(roleId),
          onToggle: () => setCollapsed((c) => {
            const next = new Set(c);
            if (next.has(roleId)) next.delete(roleId);
            else next.add(roleId);
            return next;
          }),
        };
      }}
      // The total row stands apart in the accent tint.
      highlightedId="total"
      density="compact"
    />
  );
}
